/**
 * routeGeo — projection and chainage maths for the satellite progress plan.
 *
 * The asset carries a route as `routeCoords: [{lat, lng}, …]` (imported from
 * KMZ/KML) and a chainage range. To colour a section of work on the map we
 * need to walk the polyline to the chainage where that section starts, and
 * again to where it ends, then draw only that run.
 *
 * Chainage is mapped onto the route *proportionally* rather than absolutely:
 * a surveyed route is rarely exactly (endChainage − startChainage) metres
 * long, and stretching it to fit keeps the start and end pinned where the
 * user expects them.
 *
 * No React, no Firestore — all of this is testable on its own.
 */

const EARTH_RADIUS_M = 6378137;
const TILE_SIZE = 256;
const toRad = (d) => (d * Math.PI) / 180;

/* ------------------------------------------------------------------ */
/* Distance along a route                                              */
/* ------------------------------------------------------------------ */

/** Great-circle distance between two {lat,lng} points, in metres. */
export function haversineMetres(a, b) {
  if (!a || !b) return 0;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Cumulative distance at each vertex; index 0 is always 0. */
export function cumulativeDistances(coords) {
  const cum = [0];
  for (let i = 1; i < (coords?.length || 0); i += 1) {
    cum.push(cum[i - 1] + haversineMetres(coords[i - 1], coords[i]));
  }
  return cum;
}

export const routeLength = (coords) => {
  const cum = cumulativeDistances(coords);
  return cum[cum.length - 1] || 0;
};

/** Linear interpolation between two coordinates. */
export function lerpCoord(a, b, t) {
  return { lat: a.lat + (b.lat - a.lat) * t, lng: a.lng + (b.lng - a.lng) * t };
}

/** The point sitting `dist` metres along the route. */
export function pointAtDistance(coords, cum, dist) {
  if (!coords?.length) return null;
  const total = cum[cum.length - 1] || 0;
  const d = Math.max(0, Math.min(dist, total));
  for (let i = 1; i < coords.length; i += 1) {
    if (cum[i] >= d) {
      const span = cum[i] - cum[i - 1];
      const t = span > 0 ? (d - cum[i - 1]) / span : 0;
      return lerpCoord(coords[i - 1], coords[i], t);
    }
  }
  return coords[coords.length - 1];
}

/**
 * The run of the polyline between two distances, with the end vertices
 * interpolated so the run starts and stops exactly on the chainage asked for.
 */
export function subPath(coords, cum, from, to) {
  if (!coords?.length) return [];
  const total = cum[cum.length - 1] || 0;
  const d0 = Math.max(0, Math.min(from, to, total));
  const d1 = Math.max(0, Math.min(Math.max(from, to), total));
  if (d1 <= d0) {
    const p = pointAtDistance(coords, cum, d0);
    return p ? [p] : [];
  }

  const out = [pointAtDistance(coords, cum, d0)];
  for (let i = 0; i < coords.length; i += 1) {
    if (cum[i] > d0 && cum[i] < d1) out.push(coords[i]);
  }
  out.push(pointAtDistance(coords, cum, d1));
  return out;
}

/* ------------------------------------------------------------------ */
/* Chainage ↔ route distance                                           */
/* ------------------------------------------------------------------ */

/**
 * Map a chainage onto a distance along the route. `assetStart`/`assetEnd`
 * define the chainage range the whole route covers.
 */
export function chainageToDistance(chainage, { assetStart, assetEnd, totalRouteLength }) {
  const span = Number(assetEnd) - Number(assetStart);
  if (!Number.isFinite(span) || span <= 0) return 0;
  const frac = (Number(chainage) - Number(assetStart)) / span;
  return Math.max(0, Math.min(1, frac)) * (totalRouteLength || 0);
}

/** The polyline run covering a chainage range. */
export function chainageSubPath(coords, cum, { assetStart, assetEnd, chainageStart, chainageEnd }) {
  const totalRouteLength = cum[cum.length - 1] || 0;
  const opts = { assetStart, assetEnd, totalRouteLength };
  return subPath(
    coords,
    cum,
    chainageToDistance(chainageStart, opts),
    chainageToDistance(chainageEnd, opts)
  );
}

/**
 * Build a drawable route from an asset: the imported KMZ polyline when there
 * is one, otherwise the straight line between the start and end GPS anchors.
 * Returns null when the asset has no geography at all.
 */
export function assetRoute(asset) {
  const coords = (asset?.routeCoords || [])
    .map((c) => ({ lat: Number(c.lat), lng: Number(c.lng) }))
    .filter((c) => Number.isFinite(c.lat) && Number.isFinite(c.lng));

  if (coords.length >= 2) {
    return { coords, cum: cumulativeDistances(coords), source: "kmz" };
  }

  const a = { lat: Number(asset?.latStart), lng: Number(asset?.lngStart) };
  const b = { lat: Number(asset?.latEnd), lng: Number(asset?.lngEnd) };
  if ([a.lat, a.lng, b.lat, b.lng].every(Number.isFinite)) {
    const straight = [a, b];
    return { coords: straight, cum: cumulativeDistances(straight), source: "anchors" };
  }

  return null;
}

/* ------------------------------------------------------------------ */
/* Web Mercator projection                                             */
/* ------------------------------------------------------------------ */

/** World pixel coordinates at a given zoom (256 px tiles). */
export function project(lat, lng, zoom) {
  const scale = TILE_SIZE * 2 ** zoom;
  const x = ((lng + 180) / 360) * scale;
  const s = Math.sin(toRad(Math.max(-85.05112878, Math.min(85.05112878, lat))));
  const y = (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * scale;
  return { x, y };
}

/** Inverse of `project` — world pixels back to lat/lng. */
export function unproject(x, y, zoom) {
  const scale = TILE_SIZE * 2 ** zoom;
  const lng = (x / scale) * 360 - 180;
  const n = Math.PI - 2 * Math.PI * (y / scale);
  const lat = (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
  return { lat, lng };
}

export function boundsOf(coords) {
  if (!coords?.length) return null;
  let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
  coords.forEach((c) => {
    minLat = Math.min(minLat, c.lat);
    maxLat = Math.max(maxLat, c.lat);
    minLng = Math.min(minLng, c.lng);
    maxLng = Math.max(maxLng, c.lng);
  });
  return { minLat, maxLat, minLng, maxLng };
}

/**
 * The highest zoom at which `bounds` still fits inside a width × height
 * viewport, with a little padding so the route never touches the edge.
 */
export function fitZoom(bounds, width, height, { padding = 48, maxZoom = 20, minZoom = 1 } = {}) {
  if (!bounds) return minZoom;
  const w = Math.max(width - padding * 2, 32);
  const h = Math.max(height - padding * 2, 32);

  for (let z = maxZoom; z >= minZoom; z -= 1) {
    const a = project(bounds.maxLat, bounds.minLng, z);
    const b = project(bounds.minLat, bounds.maxLng, z);
    if (Math.abs(b.x - a.x) <= w && Math.abs(b.y - a.y) <= h) return z;
  }
  return minZoom;
}

export function boundsCentre(bounds) {
  if (!bounds) return null;
  return {
    lat: (bounds.minLat + bounds.maxLat) / 2,
    lng: (bounds.minLng + bounds.maxLng) / 2,
  };
}

/** Metres per screen pixel at a latitude and zoom — drives the scale bar. */
export function metresPerPixel(lat, zoom) {
  return (Math.cos(toRad(lat)) * 2 * Math.PI * EARTH_RADIUS_M) / (TILE_SIZE * 2 ** zoom);
}

/**
 * The XYZ tiles needed to cover a viewport, given the world-pixel position of
 * the viewport's top-left corner.
 */
export function tilesForViewport({ originX, originY, width, height, zoom }) {
  const n = 2 ** zoom;
  const x0 = Math.floor(originX / TILE_SIZE);
  const y0 = Math.floor(originY / TILE_SIZE);
  const x1 = Math.floor((originX + width) / TILE_SIZE);
  const y1 = Math.floor((originY + height) / TILE_SIZE);

  const tiles = [];
  for (let y = y0; y <= y1; y += 1) {
    if (y < 0 || y >= n) continue;
    for (let x = x0; x <= x1; x += 1) {
      // Wrap horizontally so panning across the antimeridian keeps working.
      const wrapped = ((x % n) + n) % n;
      tiles.push({
        key: `${zoom}/${wrapped}/${y}`,
        x: wrapped,
        y,
        z: zoom,
        left: x * TILE_SIZE - originX,
        top: y * TILE_SIZE - originY,
      });
    }
  }
  return tiles;
}

export const TILE_PX = TILE_SIZE;
